import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { computeTipPosition, PANEL_MAX_WIDTH, VIEWPORT_MARGIN } from './infoTipPosition';

/**
 * InfoTip —— 小标题旁的「？」气泡说明
 *
 * 为什么要做成气泡而不是直接铺在页面上：
 * 设置页是给老人用的，长说明会把页面撑得很长，想找某个开关要滚半天。
 * 把「解释」折叠进气泡，页面只留「标题 + 一行摘要 + 开关」。
 *
 * 交互：
 * - 鼠标设备：移上去就显示，移开收起（不需要点）
 * - 触屏设备：点一下显示，再点一下 / 点页面别处 / 按 Esc 收起
 * - 键盘：Tab 到按钮即显示，Esc 收起
 *
 * 定位：
 * 面板用 fixed 定位并随按钮位置实时计算，所以永远不会被卡片裁掉，
 * 也不会在小屏上溢出屏幕边缘（右空间不够就向左展开，再不够就夹紧到视口内）。
 * 下方空间不足时自动翻到按钮上方。
 */
export default function InfoTip({ title, children, buttonLabel = '查看说明' }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false); // 点击锁定（触屏用）
  const [focused, setFocused] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // 只有真正支持悬停的设备才用 hover 开合；触屏靠点击，避免状态卡住
  const [canHover] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover)').matches
  );

  const open = hovered || pinned || focused;

  /** 依据「？」按钮的实时位置计算气泡落点 */
  const updatePosition = useCallback(() => {
    const btn = btnRef.current;
    const panel = panelRef.current;
    if (!btn || !panel) return;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const { left, top, width, placement } = computeTipPosition({
      btnRect: btn.getBoundingClientRect(),
      panelHeight: panel.offsetHeight || 0,
      vw,
      vh,
    });

    // 坐标没变就不更新 state，避免无意义的重渲染
    setPos((prev) => (
      prev && prev.left === left && prev.top === top && prev.width === width && prev.placement === placement
        ? prev
        : { left, top, width, placement }
    ));
  }, []);

  // 打开前先量好尺寸再定位，避免第一帧闪到错误位置
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // 打开期间跟着窗口变化重算（含滚动，用捕获阶段才能收到内部容器的滚动）
  useEffect(() => {
    if (!open) return undefined;
    const onChange = () => updatePosition();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, updatePosition]);

  // 点击别处 / 按 Esc 收起锁定的气泡
  useEffect(() => {
    if (!pinned) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setPinned(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPinned(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  const panelStyle = pos
    ? { left: pos.left, top: pos.top, width: pos.width }
    : { left: VIEWPORT_MARGIN, top: VIEWPORT_MARGIN, width: PANEL_MAX_WIDTH };

  return (
    <span
      ref={wrapRef}
      className={`info-tip${open ? ' is-open' : ''}`}
      onMouseEnter={canHover ? () => setHovered(true) : undefined}
      onMouseLeave={canHover ? () => setHovered(false) : undefined}
      onBlur={(e) => {
        if (!wrapRef.current || !wrapRef.current.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <button
        ref={btnRef}
        type="button"
        className="info-tip-btn"
        aria-label={buttonLabel}
        aria-expanded={open}
        onClick={() => setPinned((v) => !v)}
        onFocus={() => setFocused(true)}
      >
        ?
      </button>

      <span
        ref={panelRef}
        className="info-tip-panel"
        style={panelStyle}
        data-placement={pos ? pos.placement : 'bottom'}
        role="note"
        aria-hidden={!open}
      >
        {title ? <span className="info-tip-title">{title}</span> : null}
        {children}
      </span>
    </span>
  );
}
